import {
    ClauseChecklistRefreshUnconfirmedError,
    useDeleteClauseChecklist,
} from "@/api";
import { DestructiveDialog } from "@/components/shared/dialogs/destructive-dialog/DestructiveDialog";
import { DestructiveDialogProps } from "@/components/shared/dialogs/destructive-dialog/DestructiveDialog.types";
import { Delete } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-pro";
import { useQueryClient } from "@tanstack/react-query";
import { useDialogs } from "@toolpad/core";

const DELETE_UNCONFIRMED =
    "The checklist was deleted, but some views couldn't refresh.";

interface IProps {
    /**
     * The ID of the clause checklist to delete.
     */
    clauseChecklistId: number;
}

/**
 * A button that, when clicked, opens a dialog to confirm deletion of a clause checklist.
 */
export const DeleteClauseChecklistButton = ({ clauseChecklistId }: IProps) => {
    const dialogs = useDialogs();
    const queryClient = useQueryClient();
    const deletion = useDeleteClauseChecklist();

    const dialogProps: DestructiveDialogProps = {
        itemId: clauseChecklistId,
        subject: "clause checklist",
        mutationFn: deletion.mutateAsync,
        unconfirmedMessage: (error) =>
            error instanceof ClauseChecklistRefreshUnconfirmedError
                ? DELETE_UNCONFIRMED
                : null,
        onSucceeded: () => {
            queryClient.invalidateQueries({
                queryKey: ["admin-clause-checklists"],
            });
        },
    };

    const handleDelete = async () => {
        await dialogs.open(DestructiveDialog, dialogProps);
    };

    return (
        <GridActionsCellItem
            icon={
                <Tooltip title="Delete Clause Checklist">
                    <Delete />
                </Tooltip>
            }
            label="Delete Clause Checklist"
            onClick={handleDelete}
        />
    );
};
